import { useCallback, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { streamChat } from '../lib/api'
import { expandPresetsToTags } from '../lib/categoryPresets'
import type {
  AttachedDoc,
  ChatTurn,
  ChatTurnMsg,
  Mode,
  Source,
  TurnStats,
  VerificationReport,
} from '../lib/types'

export interface ChatSettings {
  mode: Mode
  top_k: number
  phrase: boolean
  temperature: number
  /** Selected category-preset keys (see `lib/categoryPresets.ts`).
   *  Expanded to raw tag strings just before the request leaves the
   *  hook, so consumers (Sidebar, App) only ever deal with stable keys. */
  categoryPresets: string[]
  /** Raw category tags to send directly (bypasses preset expansion).
   *  When set, these are merged with expanded presets. */
  rawCategories?: string[]
}

/** Live state for the answer currently being generated. */
export interface PendingTurn {
  question: string
  partial: string
  /** Streamed chain-of-thought, accumulated before/alongside the answer. */
  reasoning: string
  /** Structured operation steps showing AI's workflow */
  steps: Array<{
    action: string
    status: 'start' | 'done'
    details: Record<string, any>
  }>
  sources: Source[]
  stats: TurnStats
  /** Set once the backend's `verification` event arrives — happens
   *  after the last token but before `done`.  Undefined while the
   *  LLM is still streaming. */
  verification?: VerificationReport
}

export interface UseChatResult {
  history: ChatTurn[]
  pending: PendingTurn | null
  error: string | null
  isStreaming: boolean
  send: (question: string, settings: ChatSettings, priorHistory?: ChatTurn[], matterId?: string) => Promise<void>
  stop: () => void
  clear: () => void
  /** Documents the user uploaded for cross-corpus analysis.
   *  Order is preserved — the backend uses it as Attachment 1…N. */
  attachedDocs: AttachedDoc[]
  /** Functional / direct setter for the attachment list.  Mirrors
   *  React's `useState` signature so the consumer can append, remove
   *  or reset:
   *    - replace:  `setAttachedDocs([])`
   *    - append:   `setAttachedDocs(prev => [...prev, newDoc])`
   *    - remove:   `setAttachedDocs(prev => prev.filter(d => d.doc_id !== id))`
   */
  setAttachedDocs: Dispatch<SetStateAction<AttachedDoc[]>>
  setHistory: Dispatch<SetStateAction<ChatTurn[]>>
}

/**
 * Multi-turn chat state machine.
 *
 * Design choice (matches `scripts/chat_http.py`):
 *   - On success, BOTH the user message and the assistant reply are
 *     committed to `history` atomically.
 *   - On failure / abort, the partial reply is discarded; `history` is
 *     untouched so the user can retry.
 *
 * The hook keeps an AbortController so the UI can cancel an in-flight
 * generation.  Only one stream is allowed at a time.
 */
export function useChat(): UseChatResult {
  const [history, setHistory] = useState<ChatTurn[]>([])
  const [pending, setPending] = useState<PendingTurn | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([])
  const abortRef = useRef<AbortController | null>(null)

  // Mirror state in a ref so the streamChat closure (frozen at send-time)
  // can still see the *current* attachments without forcing a re-render
  // of the hook on every keystroke.
  const attachedDocsRef = useRef<AttachedDoc[]>([])
  attachedDocsRef.current = attachedDocs

  const send = useCallback(
    async (question: string, settings: ChatSettings, priorHistory?: ChatTurn[], matterId?: string) => {
      const q = question.trim()
      if (!q || abortRef.current) return

      setError(null)
      setIsStreaming(true)

      // Snapshot history at send time (server expects strict user/assistant
      // alternation; we never mutate it mid-stream).
      // If priorHistory is provided (e.g. loaded from a saved conversation),
      // prepend it so the RAG backend has full multi-turn context.
      const allHistory = [...(priorHistory ?? []), ...history]
      const historySnapshot: ChatTurnMsg[] = allHistory.map((t) => ({
        role: t.role,
        content: t.content,
      }))

      const draft: PendingTurn = {
        question: q,
        partial: '',
        reasoning: '',
        steps: [],
        sources: [],
        stats: {},
      }
      setPending(draft)

      const ctrl = new AbortController()
      abortRef.current = ctrl

      // Snapshot the attachment list at send-time so a mid-stream
      // add/remove does not change what the server is generating against.
      const attachmentIdsSnapshot =
        attachedDocsRef.current.map((d) => d.doc_id)

      // Expand the user-friendly preset keys into the raw tag list the
      // backend wants on `SearchRequest.categories`.  Empty list → null
      // so the request body stays minimal.
      const presetTags = expandPresetsToTags(settings.categoryPresets)
      const rawTags = settings.rawCategories ?? []
      const categoryTags = [...new Set([...presetTags, ...rawTags])]

      try {
        for await (const evt of streamChat(
          {
            question: q,
            history: historySnapshot,
            mode: settings.mode,
            top_k: settings.top_k,
            phrase: settings.phrase,
            temperature: settings.temperature,
            // Wire payload stays tidy: only send when non-empty.
            attachment_ids: attachmentIdsSnapshot.length
              ? attachmentIdsSnapshot
              : null,
            categories: categoryTags.length ? categoryTags : null,
            matter_id: matterId || null,
          },
          ctrl.signal,
        )) {
          if (evt.type === 'sources') {
            draft.sources = evt.data.sources
            draft.stats.retrieval_elapsed_s = evt.data.retrieval_elapsed_s
            setPending({ ...draft })
          } else if (evt.type === 'token') {
            draft.partial += evt.data.content
            setPending({ ...draft })
          } else if (evt.type === 'reasoning') {
            draft.reasoning += evt.data.content
            setPending({ ...draft })
          } else if (evt.type === 'step') {
            draft.steps.push({
              action: evt.data.action,
              status: evt.data.status,
              details: evt.data.details,
            })
            setPending({ ...draft })
          } else if (evt.type === 'usage') {
            draft.stats.prompt_tokens = evt.data.prompt_tokens
            draft.stats.completion_tokens = evt.data.completion_tokens
            setPending({ ...draft })
          } else if (evt.type === 'verification') {
            // Grounding report — attach to the live draft so the
            // assistant bubble can flash the warning chip the moment
            // the verifier finishes, even if the user is still
            // reading the streamed text.
            draft.verification = evt.data
            setPending({ ...draft })
          } else if (evt.type === 'done') {
            draft.stats.generation_elapsed_s = evt.data.generation_elapsed_s
            draft.stats.total_elapsed_s = evt.data.total_elapsed_s
            // Commit both turns atomically.
            const committedAnswer = draft.partial
            const committedReasoning = draft.reasoning
            const committedSteps = draft.steps
            const committedSources = draft.sources
            const committedStats = { ...draft.stats }
            const committedVerification = draft.verification
            setHistory((h) => [
              ...h,
              { role: 'user', content: q },
              {
                role: 'assistant',
                content: committedAnswer,
                reasoning: committedReasoning || undefined,
                steps: committedSteps.length > 0 ? committedSteps : undefined,
                sources: committedSources,
                stats: committedStats,
                verification: committedVerification,
              },
            ])
            setPending(null)
          } else if (evt.type === 'error') {
            throw new Error(evt.data.error || 'Unknown server error')
          }
        }
      } catch (e: unknown) {
        const isAbort =
          e instanceof DOMException && e.name === 'AbortError'
        if (!isAbort) {
          const msg = e instanceof Error ? e.message : String(e)
          setError(msg)
        }
        setPending(null)
      } finally {
        setIsStreaming(false)
        abortRef.current = null
      }
    },
    [history],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const clear = useCallback(() => {
    abortRef.current?.abort()
    setHistory([])
    setPending(null)
    setError(null)
    // "New chat" detaches every attachment from the next conversation,
    // but leaves the upload files on the server.  The user (or a future
    // "Recent uploads" panel) can still re-attach within the 14-day TTL
    // window; the periodic GC reclaims anything that's truly abandoned.
    // We deliberately *don't* fire DELETE here anymore — silently
    // destroying user-uploaded artefacts on a routine UI action turned
    // out to be a footgun (see commit history of this file).
    setAttachedDocs([])
  }, [])

  return {
    history,
    pending,
    error,
    isStreaming,
    send,
    stop,
    clear,
    attachedDocs,
    setAttachedDocs,
    setHistory,
  }
}
